/* eslint-disable @next/next/no-img-element */

import {
  MatchDetailItemFragment,
  MatchRosterDetailItemFragment,
  MatchRosterPlayerFragment,
} from '@amf/shared/types/graphql';
import classNames from 'classnames';
import Link from 'next/link';
import { useTranslate } from '@tolgee/react';
import React, { Fragment, useMemo } from 'react';
import Tippy from '@tippyjs/react';
import { getInitials } from '@amf/shared/utils/string';

interface MatchRosterTableProps {
  type: 'home' | 'visiting';
  prefixes: { player: string; team: string };
  roster?: MatchRosterDetailItemFragment;
  facr?: boolean;
}

function MatchRosterTable({ type, prefixes, roster, facr }: MatchRosterTableProps) {
  const { t } = useTranslate();

  const players = useMemo<MatchRosterPlayerFragment[]>(() => {
    if (!roster) return [];
    return roster.players.slice().sort((a, b) => (a.number > b.number ? 1 : -1));
  }, [roster]);

  if (!roster) {
    return (
      <div
        className={classNames(
          'MatchRosterList__roster',
          `MatchRosterList__roster--${type}`,
          'MatchRosterList__roster--empty',
        )}
      >
        {t('match.roster.empty')}
      </div>
    );
  }

  return (
    <div className={classNames('MatchRosterList__roster', `MatchRosterList__roster--${type}`)}>
      <Link href={`${prefixes.team}/${roster.team.id}`}>
        <a className='MatchRosterList__team'>
          <h4 className='MatchRosterList__teamName'>
            {roster.team.club.brandUrl && (
              <img
                className='MatchRosterList__teamBrand'
                src={roster.team.club.brandUrl}
                alt={roster.team.club.name}
              />
            )}
            {!roster.team.club.brandUrl && <div className='MatchRosterList__teamBrand' />}
            <span>{roster.team.name}</span>
          </h4>
          <span className='MatchRosterList__teamType'>{t(`match.roster.${type}`)}</span>
        </a>
      </Link>
      {players.length === 0 && (
        <div className='MatchRosterList__empty'>{t('match.roster.empty')}</div>
      )}
      {players.length > 0 && (
        <div className='MatchRosterList__list'>
          {players.map(player => (
            <Link key={player.id} href={`${prefixes.player}/${player.id}`}>
              <a className='MatchRosterList__player'>
                <span className='MatchRosterList__playerNumber'>{player.number}</span>
                {player.player.photoUrl ? (
                  <img
                    src={player.player.photoUrl}
                    alt={player.player.familyName}
                    className='MatchRosterList__playerAvatar'
                  />
                ) : (
                  <div className='MatchRosterList__playerAvatar'>
                    {getInitials(player.player.givenName, player.player.familyName)}
                  </div>
                )}
                <strong className='MatchRosterList__playerName'>
                  {player.player.fullName}
                  {player.player.facr && facr ? <> (FAČR)</> : null}
                  {player.goalKeeper && <span className='MatchRosterList__playerBadge'>GK</span>}
                  {player.id === roster.captain?.id && (
                    <span className='MatchRosterList__playerBadge MatchRosterList__playerBadge--green'>
                      C
                    </span>
                  )}
                </strong>
                {[...Array(player.yellowCards)].map((_, index) => (
                  <Fragment key={index}>
                    <Tippy
                      content={t('match.roster.yellowCard')}
                      animation='shift-away'
                      theme='light'
                    >
                      <span className='MatchRosterList__playerInfo'>
                        <i className='bi bi-file-fill MatchRosterList__playerInfo--yellow' />
                      </span>
                    </Tippy>
                  </Fragment>
                ))}
                {player.redCardAfterYellow && (
                  <Tippy
                    content={t('match.roster.redCardAfterYellow')}
                    animation='shift-away'
                    theme='light'
                  >
                    <span className='MatchRosterList__playerInfo'>
                      <i className='MatchRosterList__playerInfo--redYellow MatchRosterList__playerInfo--redYellow--small' />
                    </span>
                  </Tippy>
                )}
                {player.redCard && !player.redCardAfterYellow && (
                  <Tippy content={t('match.roster.redCard')} animation='shift-away' theme='light'>
                    <span className='MatchRosterList__playerInfo'>
                      <i className='bi bi-file-fill MatchRosterList__playerInfo--red' />
                    </span>
                  </Tippy>
                )}
                {[...Array(player.assists)].map((_, index) => (
                  <Tippy
                    key={`assists-${index}`}
                    content={t('match.roster.assist')}
                    animation='shift-away'
                    theme='light'
                  >
                    <span className='MatchRosterList__playerInfo'>
                      <i className='icons-xbox-a' />
                    </span>
                  </Tippy>
                ))}
                {[...Array(player.goals)].map((_, index) => (
                  <Tippy
                    key={`goals-${index}`}
                    content={t('match.roster.goal')}
                    animation='shift-away'
                    theme='light'
                  >
                    <span className='MatchRosterList__playerInfo'>
                      <i className='icons-soccer-goal' />
                    </span>
                  </Tippy>
                ))}
                <i className='MatchRosterList__playerChevron bi bi-chevron-right' />
              </a>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

interface MatchRosterListProps {
  match: MatchDetailItemFragment;
  prefixes: { player: string; team: string };
  title?: string;
  center?: boolean;
  facr?: boolean;
}

function MatchRosterList({ match, prefixes, title, center, facr }: MatchRosterListProps) {
  return (
    <div className='MatchRosterList'>
      {title && (
        <h2
          className={classNames('MatchRosterList__title', {
            'MatchRosterList__title--center': center,
          })}
        >
          {title}
        </h2>
      )}
      <div className='MatchRosterList__content'>
        <MatchRosterTable type='home' roster={match.home} prefixes={prefixes} facr={facr} />
        <MatchRosterTable type='visiting' roster={match.visiting} prefixes={prefixes} facr={facr} />
      </div>
    </div>
  );
}

export default MatchRosterList;
